/** Helpers to assemble RawFrames from decoded pixel buffers. */
import type { EncodingHint, PixelArray, RawFrame, SourceMeta } from '../core/types';

export interface FrameInput {
  width: number;
  height: number;
  /** Interleaved samples with `channels` per pixel (1..4). */
  data: PixelArray;
  channels: number;
  /** Largest code value (255, 65535, 4095...) for integer data; ignored for float. */
  maxCode?: number;
  isFloat?: boolean;
  bitDepth: number;
  format: string;
  name: string;
  encoding: EncodingHint;
  info?: Record<string, string>;
  warnings?: string[];
  pixelsPerMm?: number;
  /** Treat 2 channels as grey + alpha and 4 as RGB + alpha (default true). */
  hasAlpha?: boolean;
}

export const defaultHint = (description: string, transfer: EncodingHint['transfer'] = 'srgb'): EncodingHint => ({
  transfer,
  primaries: 'srgb',
  source: 'default',
  description,
});

/** Build a RawFrame, dropping alpha (with a warning if any pixel is not opaque). */
export function makeFrame(inp: FrameInput): RawFrame {
  const { width, height, channels } = inp;
  const n = width * height;
  const isFloat = !!inp.isFloat;
  const maxCode = isFloat ? 1 : (inp.maxCode ?? (inp.data instanceof Uint16Array ? 65535 : 255));
  const warnings = [...(inp.warnings ?? [])];
  let outCh: 1 | 3;
  let data: PixelArray;
  if (channels === 1 || channels === 3) {
    outCh = channels;
    data = inp.data;
  } else if (channels === 2 || channels === 4) {
    outCh = channels === 2 ? 1 : 3;
    const Ctor = inp.data.constructor as { new (n: number): PixelArray };
    data = new Ctor(n * outCh);
    let translucent = false;
    const opaque = isFloat ? 1 : maxCode;
    for (let i = 0; i < n; i++) {
      for (let c = 0; c < outCh; c++) data[i * outCh + c] = inp.data[i * channels + c];
      if (inp.data[i * channels + channels - 1] < opaque) translucent = true;
    }
    if (translucent) warnings.push('Alpha channel ignored: transparent pixels are measured with their stored colour values.');
  } else {
    throw new Error(`Unsupported channel count: ${channels}`);
  }
  const meta: SourceMeta = {
    name: inp.name,
    format: inp.format,
    origin: 'file',
    bitDepth: inp.bitDepth,
    encoding: inp.encoding,
    pixelsPerMm: inp.pixelsPerMm,
    info: { ...(inp.info ?? {}) },
    warnings,
    loadedAt: new Date().toISOString(),
  };
  return { width, height, channels: outCh, data, scale: 1 / maxCode, maxCode, isFloat, clipLevel: isFloat ? Infinity : 1, meta };
}

/** Convert any numeric array to Float32Array (for signed / 32-bit / float64 sources). */
export function toFloat32(a: ArrayLike<number>, scale = 1): Float32Array {
  const out = new Float32Array(a.length);
  for (let i = 0; i < a.length; i++) out[i] = a[i] * scale;
  return out;
}

/** Flip rows vertically in place. */
export function flipRows(data: PixelArray, width: number, height: number, channels: number): void {
  const row = width * channels;
  const tmp = new (data.constructor as { new (n: number): PixelArray })(row);
  for (let y = 0; y < height >> 1; y++) {
    const a = y * row;
    const b = (height - 1 - y) * row;
    tmp.set(data.subarray(a, a + row));
    data.copyWithin(a, b, b + row);
    data.set(tmp, b);
  }
}

/**
 * Apply a TIFF/EXIF Orientation (1..8) so row 0 is the visual top and column 0
 * the visual left. Returns the new pixel data and dimensions.
 */
export function applyOrientation(
  data: PixelArray,
  width: number,
  height: number,
  channels: number,
  orientation: number,
): { data: PixelArray; width: number; height: number } {
  if (!(orientation >= 2 && orientation <= 8)) return { data, width, height };
  const swap = orientation >= 5;
  const W = swap ? height : width;
  const H = swap ? width : height;
  const out = new (data.constructor as { new (n: number): PixelArray })(data.length);
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      // Source (stored) coordinates of visual pixel (x, y).
      let sx: number;
      let sy: number;
      switch (orientation) {
        case 2:
          sx = width - 1 - x;
          sy = y;
          break;
        case 3:
          sx = width - 1 - x;
          sy = height - 1 - y;
          break;
        case 4:
          sx = x;
          sy = height - 1 - y;
          break;
        case 5:
          sx = y;
          sy = x;
          break;
        case 6:
          sx = y;
          sy = height - 1 - x;
          break;
        case 7:
          sx = width - 1 - y;
          sy = height - 1 - x;
          break;
        default:
          sx = width - 1 - y;
          sy = x;
          break; // 8
      }
      const o = (y * W + x) * channels;
      const i = (sy * width + sx) * channels;
      for (let c = 0; c < channels; c++) out[o + c] = data[i + c];
    }
  return { data: out, width: W, height: H };
}
